import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, Result } from "@mimber/ui-web"
import {
  DollarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from "@mimber/ui-web"
import { useMemberInvoices } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { InvoiceWithRelationsDto } from "@mimber/schema"
import { InvoiceStatus, InvoiceSourceType } from "@mimber/schema"
import { formatCurrency } from "@/utils"
import type { MemberInvoicesTableProps } from "./MemberInvoicesTable.types"
import {
  SOURCE_LABELS,
  STATUS_COLORS,
} from "../InvoicesTable/InvoicesTable.utils"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
  getRememberedTableErrorAction,
} from "@/utils"

const { Text } = Typography

export const MemberInvoicesTable: React.FunctionComponent<
  MemberInvoicesTableProps
> = ({ memberUuid, onRowClick, className, ...tableProps }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "member-invoices",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
      scope: {
        memberUuid,
      },
    })

  const {
    data: response,
    isLoading,
    error,
  } = useMemberInvoices(memberUuid, queryParams)

  const columns: ColumnsType<InvoiceWithRelationsDto> = useMemo(
    () => [
      {
        title: "Invoice #",
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        ...getColumnSearchProps("Search invoice number"),
        render: (invoiceNumber: string | null) => (
          <Space>
            <FileTextOutlined />
            <Text>{invoiceNumber || "—"}</Text>
          </Space>
        ),
      },
      {
        title: "Source",
        dataIndex: "sourceType",
        key: "sourceType",
        render: (sourceType: InvoiceSourceType | null) =>
          sourceType ? (
            <Text>{SOURCE_LABELS[sourceType] ?? sourceType}</Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
        filters: Object.values(InvoiceSourceType).map((type) => ({
          text: SOURCE_LABELS[type] ?? type,
          value: type,
        })),
      },
      {
        title: "Amount",
        key: "totalAmount",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (_: unknown, record: InvoiceWithRelationsDto) => (
          <Space>
            <DollarOutlined />
            <Text>{formatCurrency(record.totalAmount)}</Text>
          </Space>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: InvoiceStatus) => (
          <Tag color={STATUS_COLORS[status] ?? "default"}>{status}</Tag>
        ),
        filters: Object.values(InvoiceStatus).map((status) => ({
          text: status,
          value: status,
        })),
      },
      {
        title: "Due Date",
        dataIndex: "dueAt",
        key: "dueAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (dueAt: string | null) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(dueAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Issued",
        dataIndex: "issuedAt",
        key: "issuedAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (issuedAt: string | null) => (
          <Text type="secondary">{formatTableDate(issuedAt)}</Text>
        ),
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const onTableChange: TableProps<InvoiceWithRelationsDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }

    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to load member invoices"
        subTitle={error.message}
        extra={getRememberedTableErrorAction(resetQueryParams, queryParams)}
      />
    )
  }

  return (
    <>
      <Table<InvoiceWithRelationsDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: onRowClick ? { cursor: "pointer" } : undefined,
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
